import type { ApplicationStatusWithTooltipProps } from "@js/apps/jobs/components/application-status/index";
import { DateComponent } from "@js/components/date";
import { assertUnreachable } from "@js/utils";
import { DateFormats } from "@js/utils/date/types";

type ColorVariant = {
  bgColor: string;
  textColor: string;
};

type JobStatusesWithPillComponent = EnumType<typeof ENUMS.JobFreelancerStatus>;

export const getApplicationStatusColorVariant = (
  status: JobStatusesWithPillComponent,
): ColorVariant => {
  switch (status) {
    case ENUMS.JobFreelancerStatus.INVITED_BY_MATCHER:
    case ENUMS.JobFreelancerStatus.INVITED_BY_CLIENT:
      return {
        bgColor: "var(--medium-violet)",
        textColor: "var(--dark-violet)",
      };
    case ENUMS.JobFreelancerStatus.APPLICATION_SENT:
    case ENUMS.JobFreelancerStatus.ACCEPTING_APPLICATIONS:
    case ENUMS.JobFreelancerStatus.IN_REVIEW:
    case ENUMS.JobFreelancerStatus.PENDING_VERIFICATION:
    case ENUMS.JobFreelancerStatus.PENDING_BANK_INFORMATION:
    case ENUMS.JobFreelancerStatus.PENDING_APPROVAL:
    case ENUMS.JobFreelancerStatus.JOB_FILLED:
    case ENUMS.JobFreelancerStatus.JOB_ON_HOLD:
    case ENUMS.JobFreelancerStatus.JOB_CLOSED:
    case ENUMS.JobFreelancerStatus.NON_SUPPORTED_LOCATION:
    case ENUMS.JobFreelancerStatus.IN_REVIEW_AFTER_AI_INTERVIEW:
      return {
        bgColor: "var(--soft-grey)",
        textColor: "var(--dark-teal)",
      };
    case ENUMS.JobFreelancerStatus.LIVE_INTERVIEWING:
    case ENUMS.JobFreelancerStatus.AI_INTERVIEWING:
      return {
        bgColor: "var(--medium-teal)",
        textColor: "var(--dark-teal)",
      };
    case ENUMS.JobFreelancerStatus.NEW_OFFER:
    case ENUMS.JobFreelancerStatus.UPDATED_OFFER:
    case ENUMS.JobFreelancerStatus.OFFER_EXTENDED:
    case ENUMS.JobFreelancerStatus.OFFER_ACCEPTED:
      return {
        bgColor: "var(--positive-3)",
        textColor: "var(--positive-1)",
      };
    case ENUMS.JobFreelancerStatus.APPLICATION_REJECTED:
      return {
        bgColor: "var(--medium-red)",
        textColor: "var(--dark-red)",
      };
    default: {
      assertUnreachable(status);
      return {
        bgColor: "var(--soft-grey)",
        textColor: "var(--dark-teal)",
      };
    }
  }
};

const APPLICATION_STATUS_DATE_FORMAT = DateFormats["Jan 1"];

export const getApplicationTooltipText = ({
  status,
  appliedDate,
  offerCreatedDate,
  offerAcceptedDate,
  offerUpdatedDate,
  userFailedIdentityVerification,
}: Omit<ApplicationStatusWithTooltipProps, "status"> & {
  status: JobStatusesWithPillComponent;
  userFailedIdentityVerification: boolean;
}) => {
  switch (status) {
    case ENUMS.JobFreelancerStatus.APPLICATION_SENT:
    case ENUMS.JobFreelancerStatus.ACCEPTING_APPLICATIONS:
    case ENUMS.JobFreelancerStatus.INVITED_BY_MATCHER:
    case ENUMS.JobFreelancerStatus.INVITED_BY_CLIENT:
      return null;
    case ENUMS.JobFreelancerStatus.PENDING_VERIFICATION:
    case ENUMS.JobFreelancerStatus.PENDING_APPROVAL:
      return (
        <>
          Please complete the required steps shown on your Home page to continue
          your application.
        </>
      );
    case ENUMS.JobFreelancerStatus.PENDING_BANK_INFORMATION:
      return <>Please fill in your banking information</>;
    case ENUMS.JobFreelancerStatus.IN_REVIEW:
      if (!appliedDate) return null;
      return (
        <>Your application is currently under review by the hiring team.</>
      );
    case ENUMS.JobFreelancerStatus.IN_REVIEW_AFTER_AI_INTERVIEW:
      return "Your AI interview is being reviewed";
    case ENUMS.JobFreelancerStatus.LIVE_INTERVIEWING:
      return "You’ve been invited to schedule a live video interview. The hiring manager has provided their scheduling link to get started.";
    case ENUMS.JobFreelancerStatus.AI_INTERVIEWING:
      return "You’ve been invited to an AI interview. You can start it any time.";
    case ENUMS.JobFreelancerStatus.NEW_OFFER:
    case ENUMS.JobFreelancerStatus.UPDATED_OFFER:
      if (userFailedIdentityVerification) {
        return "Due to ID verification failure";
      }

      if (!offerCreatedDate) return null;

      return (
        <>
          New offer sent{" "}
          <DateComponent
            date={offerCreatedDate}
            format={APPLICATION_STATUS_DATE_FORMAT}
          />
          .
        </>
      );
    case ENUMS.JobFreelancerStatus.OFFER_EXTENDED:
      if (userFailedIdentityVerification) {
        return "Due to ID verification failure";
      }

      if (!offerUpdatedDate) return null;

      return (
        <>
          Offer updated{" "}
          <DateComponent
            date={offerUpdatedDate}
            format={APPLICATION_STATUS_DATE_FORMAT}
          />
          .
        </>
      );
    case ENUMS.JobFreelancerStatus.OFFER_ACCEPTED:
      if (!offerAcceptedDate) return null;
      return (
        <>
          Offer accepted{" "}
          <DateComponent
            date={offerAcceptedDate}
            format={APPLICATION_STATUS_DATE_FORMAT}
          />
          .
        </>
      );
    case ENUMS.JobFreelancerStatus.JOB_FILLED:
    case ENUMS.JobFreelancerStatus.JOB_ON_HOLD:
    case ENUMS.JobFreelancerStatus.JOB_CLOSED:
      return "This job is currently not accepting applications.";
    case ENUMS.JobFreelancerStatus.NON_SUPPORTED_LOCATION:
      return "This position isn't available in your location. Explore other opportunities!";
    case ENUMS.JobFreelancerStatus.APPLICATION_REJECTED:
      return "You were not selected for this position. Continue applying!";

    default: {
      assertUnreachable(status);
    }
  }
};
